import dayjs from 'dayjs'
import { LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { t, Trans } from '@lingui/macro'

import colors from '@/assets/scss/_themes-vars.module.scss'
import tableNoData from '@/assets/svg/table-no-data.svg'
import { Patient } from '@/core/models/patient.model'
import { isProvisionalIdentityValue } from '@/features/patient/patient-profile-form/lib/identity-naming'

import { InsPatientActions } from './InsPatientActions'

type InsPatientsTableProps = {
  data: Patient[]
  loading: boolean
  onOpen: (patientId: string) => void
}

const displayValue = (value?: string | null) => (isProvisionalIdentityValue(value) ? t`Unknown` : value?.trim() || t`Not provided`)
const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : t`Not provided`)

export function InsPatientsTable({ data, loading, onOpen }: InsPatientsTableProps) {
  const headers = [t`Birth surname`, t`Birth given name(s)`, t`Used surname`, t`Used given name`, t`Date of birth`, t`Sex`, t`INS matricule`]

  return (
    <Stack>
      {loading && <LinearProgress aria-label={t`Loading patients`} />}
      <Table stickyHeader id="ins-patients-table" aria-label={t`INS patient identity list`}>
        <TableHead sx={{ bgcolor: colors.gray100 }}>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                scope="col"
                sx={{
                  color: colors.gray600,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {header}
              </TableCell>
            ))}
            <TableCell align="center" scope="col">
              <Trans>Actions</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!data.length && (
            <TableRow>
              <TableCell colSpan={8}>
                <Stack alignItems="center" justifyContent="center" p="64px 24px">
                  <img alt="no-data" src={tableNoData} />
                </Stack>
              </TableCell>
            </TableRow>
          )}
          {data.map((patient, index) => (
            <TableRow key={patient._id} id={`ins-patient-row-${index}`} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {displayValue(patient.userInfo.birthSurname)}
                </Typography>
              </TableCell>
              <TableCell>{displayValue(patient.userInfo.birthName)}</TableCell>
              <TableCell>{displayValue(patient.userInfo.surname)}</TableCell>
              <TableCell>{displayValue(patient.userInfo.name)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(patient.userInfo.birthday)}</TableCell>
              <TableCell>{displayValue(patient.userInfo.gender)}</TableCell>
              <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{displayValue(patient.insNIR)}</TableCell>
              <TableCell align="center">
                <InsPatientActions patientId={patient._id} onOpen={onOpen} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  )
}
